import { ArrowRight, Bot, CircleCheck, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MetricCard from "@/components/MetricCard";
import { api } from "@/lib/api";

function Insight({ icon, title, copy, count, tone }) {
  return (
    <div className="insight-row" data-testid={`insight-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <span className={`insight-icon insight-${tone}`}>{icon}</span>
      <span><strong>{title}</strong><small>{copy}</small></span>
      <b>{count}</b>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [dashboard, setDashboard] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [activityRows, setActivityRows] = useState([]);
  const [workspaceName, setWorkspaceName] = useState("Workspace");

  useEffect(() => {
    Promise.all([api.get(`/dashboard?days=${days}`), api.get("/recommendation"), api.get("/activity"), api.get("/auth/me")])
      .then(([summary, decision, liveActivity, identity]) => {
        setDashboard(summary.data);
        setRecommendation(decision.data);
        setWorkspaceName(identity.data.workspace?.shopify_shop_name || "Shopify authorization needs attention");
        if (liveActivity.data.length) setActivityRows(liveActivity.data.map((row, index) => ["Operations", row.message, "Now", `A${index}`]));
      })
      .catch(() => undefined);
  }, [days]);

  const kpis = dashboard?.kpis || {};
  return (
    <div className="page dashboard-page" data-testid="dashboard-page">
      <section className="dashboard-welcome">
        <div>
          <span className="eyebrow"><Sparkles size={12} />POST-PURCHASE OPERATIONS</span>
          <h1 data-testid="dashboard-greeting">Good afternoon, {workspaceName} <span>✦</span></h1>
          <p data-testid="dashboard-subtitle">Here’s what’s happening with your post-purchase operations.</p>
        </div>
        <div className="heading-actions">
          <button className="primary-button" onClick={() => navigate("/app/requests?status=review")} data-testid="review-priority-requests-button">
            <ShieldCheck size={16} />Review priority requests
          </button>
          <button className="secondary-button" onClick={() => setDays(days === 7 ? 30 : 7)} data-testid="dashboard-date-range-button">Last {days} days</button>
        </div>
      </section>
      <section className="metrics-grid" data-testid="executive-kpi-strip">
        <MetricCard icon={<Bot size={17} />} label="New requests" value={kpis.new_requests ?? "—"} change="Live" />
        <MetricCard icon={<Zap size={17} />} label="Revenue retained" value={kpis.revenue_retained ? `$${kpis.revenue_retained}` : "—"} change="Live" tone="green" />
        <MetricCard icon={<CircleCheck size={17} />} label="Resolution rate" value={kpis.resolution_rate !== undefined ? `${kpis.resolution_rate}%` : "—"} change="Live" tone="violet" />
        <MetricCard icon={<ShieldCheck size={17} />} label="Value protected" value={kpis.value_protected ? `$${kpis.value_protected}` : "—"} change="Live" tone="amber" />
        <MetricCard icon={<Sparkles size={17} />} label="Automation rate" value={kpis.automation_rate !== undefined ? `${kpis.automation_rate}%` : "—"} change="Live" tone="cyan" />
      </section>
      <section className="dashboard-grid" data-testid="dashboard-analysis-grid">
        <article className="panel chart-panel" data-testid="request-volume-chart">
          <div className="panel-header"><div><h2>Request volume</h2><p>Seven-day trend across all requests</p></div><button data-testid="view-analytics-button">View analytics <ArrowRight size={14} /></button></div>
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboard?.chart || []} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
                <defs><linearGradient id="requestGradient" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#176dff" stopOpacity=".55" /><stop offset="100%" stopColor="#176dff" stopOpacity="0" /></linearGradient></defs>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#7f90b1", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#071a38", border: "1px solid #1d3b68", borderRadius: 8 }} />
                <Area type="monotone" dataKey="value" stroke="#2583ff" strokeWidth={2.5} fill="url(#requestGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>
        <article className="panel recommendation-panel" data-testid="ai-recommendation-panel">
          <div className="panel-header"><div><h2>AI Recommendation</h2><p>{recommendation?.policy_matched || "Overall confidence"}</p></div><strong className="confidence-score">{recommendation?.confidence ?? 98}%<small>High confidence</small></strong></div>
          <div className="confidence-bar"><i /></div>
          <div className="recommendation-copy"><span className="recommendation-icon"><Sparkles size={17} /></span><div><h3>{recommendation?.action?.replace("_", " ") || "Awaiting operational activity"}</h3><p>{recommendation?.reason || "Connect Shopify and sync activity to generate a policy-grounded recommendation."}</p></div></div>
          <ul data-testid="recommendation-reasons"><li><CircleCheck size={14} />Policy aligned</li><li><CircleCheck size={14} />High customer value</li><li><CircleCheck size={14} />Low fraud risk</li></ul>
          <button className="primary-button full-button" onClick={() => navigate("/app/requests?status=review")} data-testid="review-ai-recommendation-button">Review in queue <ArrowRight size={15} /></button>
        </article>
        <article className="panel insights-panel" data-testid="priority-insights-panel">
          <div className="panel-header"><div><h2>Priority insights</h2><p>Items that need attention</p></div></div>
          <Insight icon={<Sparkles size={15} />} title="Retention opportunities" copy="8 high-value customers can be retained" count="AI" tone="violet" />
          <Insight icon={<Bot size={15} />} title="Review queue" copy="2 requests need your review" count="2" tone="amber" />
          <Insight icon={<ShieldCheck size={15} />} title="Fraud alerts" copy="1 high-risk request detected" count="1" tone="red" />
          <Insight icon={<CircleCheck size={15} />} title="Policy exceptions" copy="3 requests require attention" count="3" tone="blue" />
          <button className="text-button" data-testid="view-all-insights-button">View all insights <ArrowRight size={14} /></button>
        </article>
      </section>
      <section className="dashboard-bottom">
        <article className="panel activity-panel" data-testid="recent-activity-panel">
          <div className="panel-header"><div><h2>Recent activity</h2><p>Customer requests moving through your workspace</p></div><button data-testid="view-all-activity-button">View all</button></div>
          {activityRows.length ? activityRows.map(([name, detail, time, initials]) => <div className="activity-row" key={`${name}-${initials}`} data-testid={`activity-${initials.toLowerCase()}`}><span className="customer-avatar">{initials}</span><span><strong>{name}</strong><small>{detail}</small></span><time>{time}</time></div>) : <div className="empty-state" data-testid="empty-activity-state"><h2>No operational activity yet</h2><p>Shopify activity will appear here after sync.</p></div>}
        </article>
        <article className="panel health-panel" data-testid="store-health-panel">
          <div className="panel-header"><div><h2>Store health</h2><p>Based on 30 days of operations</p></div><span className="health-score">92</span></div>
          <div className="health-ring"><span>Excellent<small>All core signals healthy</small></span></div>
          <div className="health-bars"><p><span>Resolution speed</span><b>96%</b></p><i><em /></i><p><span>Customer recovery</span><b>88%</b></p><i><em className="bar-88" /></i></div>
        </article>
      </section>
    </div>
  );
}